import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { runGated } from "@/lib/adsGate";
import { useAuth } from "@/lib/AuthProvider";
import { isNative, takeNativePhoto, dataUrlToFile } from "@/lib/native";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Camera, Upload, Loader2, Sparkles, ShieldCheck } from "lucide-react";
import { setLatestBeautyScan } from "./useLatestBeautyScan";

export default function BeautyScan() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const pickFile = async () => {
    // Native shell: use the OS camera/photo prompt so users can take a selfie
    // without the web file dialog. Falls back to the hidden <input> on web
    // or if the native camera errors (e.g. permission denied).
    if (isNative()) {
      try {
        const dataUrl = await takeNativePhoto();
        if (!dataUrl) return; // user cancelled
        const f = dataUrlToFile(dataUrl, `selfie-${Date.now()}.jpg`);
        if (f) {
          setFile(f);
          setPreview(URL.createObjectURL(f));
          return;
        }
      } catch (e) {
        if (String(e?.message || "").toLowerCase().includes("denied")) {
          toast.error("Camera permission denied.");
          return;
        }
        // fall through to web picker
      }
    }
    inputRef.current?.click();
  };

  const onSelect = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const run = async ({ useSaved }) => {
    setBusy(true);
    try {
      const form = new FormData();
      if (!useSaved && file) form.append("file", file);
      const { data } = await runGated("beauty_scan", () => api.post("/beauty/scan", form, { headers: { "Content-Type": "multipart/form-data" } }));
      // Seed the shared cache so Makeup / Hair & Beard / Care don't briefly
      // show the "no scan yet" empty state on the very next mount.
      setLatestBeautyScan({
        id: data.id,
        user_id: user?.user_id,
        analysis: data.analysis,
        created_at: data.created_at,
        deleted: false,
      });
      toast.success("Scan complete — building your beauty brief.");
      navigate(`/beauty/scans/${data.id}`, { replace: true });
    } catch (e) {
      const msg = e?.response?.data?.detail || "Scan failed — please retry with a clearer selfie.";
      toast.error(String(msg));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-10 py-10 lg:py-14" data-testid="beauty-scan-page">
      <p className="eyebrow flex items-center gap-2"><Sparkles size={12} /> AI Scan</p>
      <h1 className="editorial-h1 mt-3">One selfie. <em>Everything unlocks.</em></h1>
      <p className="text-sm text-muted-foreground mt-3 max-w-2xl">
        We analyse face, skin, hair, beard and scalp in a single pass. Every Beauty module reads from this scan — no repeat uploads.
      </p>

      <div className="mt-10 grid lg:grid-cols-[1fr,1.2fr] gap-8">
        {/* Preview */}
        <div className="border border-border p-6">
          <p className="eyebrow">Selfie</p>
          <div className="mt-3 aspect-[3/4] bg-muted flex items-center justify-center overflow-hidden">
            {preview ? (
              <img src={preview} alt="preview" className="h-full w-full object-cover" data-testid="beauty-scan-preview" />
            ) : (
              <div className="text-center p-8">
                <Camera size={36} strokeWidth={1.3} className="mx-auto opacity-40" />
                <p className="text-xs text-muted-foreground mt-4 max-w-[220px] mx-auto">
                  Well-lit · face centred · no filters. A neutral background gets the sharpest read.
                </p>
              </div>
            )}
          </div>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            capture="user"
            onChange={onSelect}
            className="hidden"
            data-testid="beauty-scan-file-input"
          />
          <Button
            onClick={pickFile}
            variant="outline"
            className="w-full mt-4 rounded-none uppercase tracking-[0.18em] text-[11px]"
            data-testid="beauty-scan-pick"
          >
            <Upload size={14} className="mr-2" /> {preview ? "Choose another" : "Take / choose selfie"}
          </Button>
        </div>

        {/* Actions */}
        <div className="space-y-6">
          <div className="border border-border p-6">
            <p className="eyebrow">Use my saved portrait</p>
            <p className="font-serif text-2xl mt-1">Skip re-uploads.</p>
            <p className="text-xs text-muted-foreground mt-2">
              We&apos;ll re-use the primary face photo from your Identity gallery — the same one Try-On uses.
            </p>
            <Button
              disabled={busy}
              onClick={() => run({ useSaved: true })}
              className="w-full mt-5 rounded-none uppercase tracking-[0.2em] text-xs h-11"
              data-testid="beauty-scan-use-saved"
            >
              {busy ? <><Loader2 size={14} className="mr-2 animate-spin" /> Analysing</> : "Analyse saved portrait"}
            </Button>
          </div>

          <div className="border border-border p-6">
            <p className="eyebrow">Use this selfie</p>
            <p className="font-serif text-2xl mt-1">Fresh reading.</p>
            <p className="text-xs text-muted-foreground mt-2">
              Uploads a new photo, runs the analysis, and adds it to your Identity gallery for future modules.
            </p>
            <Button
              disabled={busy || !file}
              onClick={() => run({ useSaved: false })}
              className="w-full mt-5 rounded-none uppercase tracking-[0.2em] text-xs h-11"
              data-testid="beauty-scan-use-new"
            >
              {busy ? <><Loader2 size={14} className="mr-2 animate-spin" /> Analysing</> : "Analyse this selfie"}
            </Button>
          </div>

          <div className="text-xs text-muted-foreground flex items-start gap-2">
            <ShieldCheck size={16} className="mt-0.5 shrink-0" />
            <p>Your selfie stays private. Analysis is stored to your account only — never shared with third parties.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
